import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { StatusBadge } from "@/components/ui/status-badge"
import { formatDateOnly } from "@/lib/datetime"
import { humanize } from "../../lib/careplan"
import type { TcmCarePlan, TcmCheckin } from "../../types"

const STATUS: Record<string, { label: string; variant: "success" | "warning" | "info" | "muted" | "destructive" }> = {
  PENDING: { label: "Not booked yet", variant: "muted" },
  SCHEDULED: { label: "Booked", variant: "info" },
  ATTEMPTED: { label: "Trying", variant: "info" },
  COMPLETED: { label: "Reached", variant: "success" },
  UNREACHED: { label: "Couldn't reach", variant: "warning" },
  SKIPPED: { label: "Skipped", variant: "muted" },
  CANCELLED: { label: "Cancelled", variant: "muted" },
}

const ZONE = {
  GREEN: { label: "Doing well", variant: "success" as const },
  YELLOW: { label: "Needed the clinic", variant: "warning" as const },
  RED: { label: "Sent to 911 / ED", variant: "destructive" as const },
}

const md = (d: string | null | undefined) => formatDateOnly(d ?? null, "EEE M/d")

/** Pear's weekly calls in the 30 days: each week's window, what it asks about, and how it went. */
export function CallsPanel({ plan }: { plan: TcmCarePlan }) {
  const weeks = [1, 2, 3, 4].map((w) => ({
    week: w,
    checkin: plan.checkins.find((c) => c.week === w),
    items: plan.checkin_plan.find((p) => p.week === w)?.items ?? [],
  }))
  return (
    <div className="space-y-3">
      <p className="text-sm text-muted-foreground">
        {plan.checkins.length
          ? "Pear calls the patient once a week for the clinic. A yellow or red answer goes to the clinic the same day."
          : "Pear's weekly calls aren't switched on for this clinic yet. The questions below are what each call will ask."}
      </p>
      <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
        {weeks.map(({ week, checkin, items }) => (
          <WeekCard key={week} week={week} checkin={checkin} items={items.map((i) => i.key)} />
        ))}
      </div>
    </div>
  )
}

function WeekCard({ week, checkin, items }: { week: number; checkin?: TcmCheckin; items: string[] }) {
  const status = checkin ? STATUS[checkin.status] ?? { label: humanize(checkin.status.toLowerCase()), variant: "muted" as const } : null
  const zone = checkin?.zone ? ZONE[checkin.zone] : null
  return (
    <Card>
      <CardHeader className="space-y-1 pb-2">
        <CardTitle className="flex items-center justify-between gap-2 text-sm">
          <span>Week {week}</span>
          {status && <StatusBadge variant={status.variant} size="sm">{status.label}</StatusBadge>}
        </CardTitle>
        {checkin && (
          <p className="text-xs text-muted-foreground">
            {md(checkin.window_start)} to {md(checkin.window_end)}
            {checkin.skip_reason ? ` · ${humanize(checkin.skip_reason.toLowerCase())}` : ""}
          </p>
        )}
      </CardHeader>
      <CardContent className="space-y-2">
        {zone && <StatusBadge variant={zone.variant} dot>{zone.label}</StatusBadge>}
        {items.length ? (
          <ul className="list-disc space-y-0.5 pl-4 text-[13px]">
            {items.map((k) => (
              <li key={k}>{humanize(k.split(".").pop() ?? k)}</li>
            ))}
          </ul>
        ) : (
          <p className="text-[13px] text-muted-foreground">No questions planned.</p>
        )}
      </CardContent>
    </Card>
  )
}
